import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Card, Input, Modal, Form, 
  notification, Tooltip, Popconfirm, Tag, Tabs, Switch, Alert
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, 
  ExclamationCircleOutlined, SearchOutlined, ReloadOutlined, LockOutlined,AlertFilled
} from '@ant-design/icons';
import permissionService from '../../../services/permissionService';
import userRoleUtils from '../../../utils/userRoleUtils';
import MyTable from '../../../components/common/MyTable';

const { Search } = Input;
const { confirm } = Modal;
const { TabPane } = Tabs;

const PermissionList = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [form] = Form.useForm();
  const [tabActiveKey, setTabActiveKey] = useState('all');

  // Check if user has permission to manage permissions
  const canManagePermissions = userRoleUtils.hasPermission('manage_permissions');

  // Load permissions on component mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  // Filter permissions based on search text and tab
  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = 
      permission.name.toLowerCase().includes(searchText.toLowerCase()) ||
      permission.description.toLowerCase().includes(searchText.toLowerCase()) ||
      (permission.group && permission.group.toLowerCase().includes(searchText.toLowerCase()));
    
    if (tabActiveKey === 'all') {
      return matchesSearch;
    } else if (tabActiveKey === 'admin') {
      return matchesSearch && permission.is_admin === 1;
    } else if (tabActiveKey === 'regular') {
      return matchesSearch && permission.is_admin !== 1;
    }
    
    return matchesSearch;
  });

  // Fetch permissions from API
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionService.getAllPermissions();
      setPermissions(response.data.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching permissions',
        description: error.message || 'Failed to load permissions'
      });
    } finally {
      setLoading(false);
    }
  };

  // Show confirm dialog before deleting
  const showDeleteConfirm = (permissionId, permissionName) => {
    confirm({
      title: 'Are you sure you want to delete this permission?',
      content: `You are about to delete the permission "${permissionName}". This action cannot be undone.`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(permissionId);
      }
    });
  };

  // Handle delete permission
  const handleDelete = async (permissionId) => {
    try {
      await permissionService.deletePermission(permissionId);
      notification.success({
        message: 'Permission deleted successfully'
      });
      fetchPermissions();
    } catch (error) {
      notification.error({
        message: 'Error deleting permission',
        description: error.message || 'Failed to delete permission'
      });
    }
  };

  // Show modal for add/edit
  const showModal = (permission = null) => {
    setEditingPermission(permission);
    
    if (permission) {
      form.setFieldsValue({
        name: permission.name,
        description: permission.description,
        group: permission.group || '',
        is_admin: permission.is_admin === 1
      });
    } else {
      form.resetFields();
      // Default is non-admin permission
      form.setFieldsValue({
        is_admin: false
      });
    }
    
    setModalVisible(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Convert is_admin boolean to 0 or 1
      values.is_admin = values.is_admin ? 1 : 0;
      
      if (editingPermission) {
        // Update existing permission
        await permissionService.updatePermission(editingPermission.id, values);
        notification.success({
          message: 'Permission updated successfully'
        });
      } else {
        // Create new permission
        await permissionService.createPermission(values);
        notification.success({
          message: 'Permission created successfully'
        });
      }
      
      setModalVisible(false);
      fetchPermissions();
    } catch (error) {
      notification.error({
        message: 'Error saving permission',
        description: error.message || 'Failed to save permission'
      });
    }
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Type',
      key: 'is_admin',
      render: (_, record) => (
        record.is_admin === 1 ? (
          <Tag color="red" icon={<AlertFilled />}>Admin</Tag>
        ) : (
          <Tag color="blue" icon={<LockOutlined />}>Regular</Tag>
        )
      ),
      filters: [
        { text: 'Admin', value: 1 },
        { text: 'Regular', value: 0 }
      ],
      onFilter: (value, record) => record.is_admin === value,
      width: 120
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
      render: group => (
        group ? <Tag color="blue">{group}</Tag> : <span className="text-gray-400">None</span>
      ),
      sorter: (a, b) => (a.group || '').localeCompare(b.group || ''),
      width: 120
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Permission">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => showModal(record)}
              disabled={!canManagePermissions}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>
          <Tooltip title="Delete Permission">
            <Popconfirm
              title="Delete this permission?"
              onConfirm={() => showDeleteConfirm(record.id, record.name)}
              okText="Yes"
              cancelText="No"
              disabled={!canManagePermissions}
            >
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
                disabled={!canManagePermissions}
                className="text-red-500 hover:text-red-700"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  // Render component
  return (
    <div className="p-6">
      <Card className="shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Permissions Management</h1>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => showModal()}
            disabled={!canManagePermissions}
            className="bg-golden-navy"
          >
            Add Permission
          </Button>
        </div>

        <div className="mb-4">
          <Tabs 
            activeKey={tabActiveKey} 
            onChange={setTabActiveKey}
            type="card"
          >
            <TabPane 
              tab={
                <span>
                  All Permissions
                  <Tag className="ml-2" color="default">{permissions.length}</Tag>
                </span>
              }
              key="all"
            />
            <TabPane 
              tab={
                <span>
                  <AlertFilled /> Admin Permissions
                  <Tag className="ml-2" color="red">
                    {permissions.filter(p => p.is_admin === 1).length}
                  </Tag>
                </span>
              }
              key="admin"
            />
            <TabPane 
              tab={
                <span>
                  <LockOutlined /> Regular Permissions
                  <Tag className="ml-2" color="blue">
                    {permissions.filter(p => p.is_admin !== 1).length}
                  </Tag>
                </span>
              }
              key="regular"
            />
          </Tabs>
        </div>

        <div className="mb-4 flex justify-between items-center">
          <Search
            placeholder="Search permissions..."
            allowClear
            onSearch={value => setSearchText(value)}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchPermissions}
          >
            Refresh
          </Button>
        </div>

        <MyTable
          rowKey="id"
          columns={columns}
          dataSource={filteredPermissions}
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered
          rowClassName={record => record.is_admin === 1 ? 'bg-red-50' : ''}
        />
      </Card>

      {/* Add/Edit Permission Modal */}
      <Modal
        title={editingPermission ? 'Edit Permission' : 'Add Permission'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleSubmit}
            className="bg-golden-navy"
          >
            {editingPermission ? 'Update' : 'Create'}
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Permission Name"
            rules={[
              { required: true, message: 'Please enter a name' },
              { 
                pattern: /^[a-z_]+$/, 
                message: 'Name must contain only lowercase letters and underscores' 
              }
            ]}
          >
            <Input placeholder="E.g., manage_users, view_reports" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea 
              placeholder="Describe what this permission allows" 
              rows={3}
            />
          </Form.Item>
          
          <Form.Item
            name="group"
            label="Group (Optional)"
            tooltip="Group related permissions together"
          >
            <Input placeholder="E.g., users, orders, reports" />
          </Form.Item>

          <Form.Item
            name="is_admin"
            label="Admin Permission"
            valuePropName="checked"
            tooltip="Admin permissions can only be assigned to admin roles"
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          
          <Alert
            type="info"
            showIcon
            message="Permission Type Information"
            description={
              <ul className="mt-2 ml-2">
                <li><strong>Admin Permissions:</strong> Can only be assigned to admin roles.</li>
                <li><strong>Regular Permissions:</strong> Can only be assigned to regular roles.</li>
              </ul>
            }
          />
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionList;